import {differenceInDays, startOfDay, startOfToday} from 'date-fns';
import {getName} from 'i18n-iso-countries';
import {Dropdown, Flag} from 'platform/components';
import {Box, Hide, HStack, Icon, Show, Text, useDevice, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {capitalize, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {FALLBACK_LANGUAGE} from '../../../../../i18n/fallbackLanguage';
import i18n from '../../../../../i18n/i18n';
import {useIsPrintForced} from '../../../../../pages/Overview/hooks/useIsPrintForced';

interface ReportInfoProps extends RequiredTestIdProps {
  created?: string;
  market?: string | null;
  couponNumber?: string | null;
}

export function ReportInfo(props: ReportInfoProps) {
  const formatDate = useDateTimeFormatter();
  const device = useDevice();
  const {isPrint} = useIsPrintForced();
  const isDesktop = device === 'desktop';
  const createdDate = parseDate(props.created);
  const dateString = isNotNil(createdDate) ? formatDate('dateShort', createdDate) : null;
  const daysCreatedAgo = isNotNil(createdDate)
    ? differenceInDays(startOfToday(), startOfDay(createdDate))
    : null;

  const marketTitle = isNotNil(props.market)
    ? (getName(props.market, i18n?.resolvedLanguage ?? FALLBACK_LANGUAGE) ??
      i18n.t(`page.overview.market.${props.market}`, {defaultValue: props.market}))
    : undefined;

  return (
    <HStack spacing={6}>
      <Show when={isNotNil(marketTitle) && (isDesktop || isPrint)}>
        <Text color="white" size="xSmall" data-testid={suffixTestId('reportMarket', props)}>
          {i18n.t('entity.priceReport.market')}: {marketTitle}
        </Text>
      </Show>

      <Show when={isNotNil(dateString) && (isDesktop || isPrint)}>
        <HStack spacing={2}>
          <Text color="white" size="xSmall" data-testid={suffixTestId('reportDate', props)}>
            {i18n.t('entity.priceReport.created')} {dateString}
          </Text>
          <Hide when={isPrint || isNil(daysCreatedAgo) || daysCreatedAgo === 0}>
            <Flag
              label={capitalize(
                i18n.t('entity.header.labels.nDaysAgo', {
                  count: daysCreatedAgo ?? 0,
                })
              )}
              size="small"
              colorScheme="yellow"
              data-testid={suffixTestId('reportDateDaysAgo', props)}
            />
          </Hide>
        </HStack>
      </Show>

      <Show when={isNotNil(props.couponNumber) && isPrint}>
        <Text color="white" size="xSmall">
          {i18n.t('entity.priceReport.couponNumber')}: {props.couponNumber}
        </Text>
      </Show>

      <Hide when={(isNil(props.couponNumber) && isDesktop) || isPrint}>
        <Dropdown
          dropdownControl={<Icon color="palettes.white.10.100" value="action/info" size={4} />}
          data-testid={suffixTestId('reportInfoDropdown', props)}
        >
          <Box padding={2}>
            <VStack spacing={2}>
              <Hide onDesktop when={isNil(marketTitle)}>
                <Text size="xSmall" data-testid={suffixTestId('reportMarket', props)}>
                  {i18n.t('entity.priceReport.market')}: {marketTitle}
                </Text>
              </Hide>
              <Hide onDesktop when={isNil(dateString)}>
                <HStack spacing={2}>
                  <Text size="xSmall" data-testid={suffixTestId('reportDate', props)}>
                    {`${i18n.t('entity.priceReport.created')} ${dateString}`}
                  </Text>
                  <Hide when={isNil(daysCreatedAgo) || daysCreatedAgo === 0}>
                    <Flag
                      label={capitalize(
                        i18n.t('entity.header.labels.nDaysAgo', {
                          count: daysCreatedAgo ?? 0,
                        })
                      )}
                      size="small"
                      colorScheme="yellow"
                      data-testid={suffixTestId('reportDateDaysAgo', props)}
                    />
                  </Hide>
                </HStack>
              </Hide>
              <Hide when={isNil(props.couponNumber)}>
                <Text size="xSmall">
                  {i18n.t('entity.priceReport.couponNumber')}: {props.couponNumber}
                </Text>
              </Hide>
            </VStack>
          </Box>
        </Dropdown>
      </Hide>
    </HStack>
  );
}
